import type { JSX } from 'react';
import { clockTime } from '@/lib/format';
import { activityGlyph } from '@/lib/glyphs';
import { failureFrom } from '@/lib/result';
import { useLatestActivity } from '../activity/use-activity';
import { button, card, glyphTile } from '../shell/look';
import { EmptyState, ErrorState } from '../shell/states';
import { navigate } from '../shell/use-route';

/** The overview's last six events (design L327–341). */
export function LiveActivity(): JSX.Element {
  const latest = useLatestActivity(6);
  return (
    <div className={`${card} px-6 py-[22px]`}>
      <div className="mb-3.5 flex items-center justify-between">
        <div className="text-[15px] font-semibold">Live activity</div>
        <button
          type="button"
          className={button.link}
          onClick={() => navigate({ page: 'activity' })}
        >
          View all →
        </button>
      </div>
      {latest.isPending && <Skeleton />}
      {latest.isError && (
        <ErrorState {...failureFrom(latest.error)} onRetry={() => void latest.refetch()} />
      )}
      {latest.data?.length === 0 && <EmptyState title="No activity yet" className="p-6" />}
      <div className="flex flex-col">
        {latest.data?.map((item) => (
          <div
            key={item.id}
            className="border-line grid grid-cols-[48px_34px_minmax(0,1fr)] items-center gap-3 border-t py-2.5"
          >
            <span className="text-mute font-mono text-[11.5px]">{clockTime(item.at)}</span>
            <span className={glyphTile}>{activityGlyph(item)}</span>
            <span className="truncate text-[13.5px]" title={item.text}>
              {item.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Skeleton(): JSX.Element {
  return (
    <div className="flex flex-col" aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="border-line flex items-center gap-3 border-t py-3">
          <div className="skeleton h-3 w-10" />
          <div className="skeleton h-6 w-[34px]" />
          <div className="skeleton h-3 flex-1" />
        </div>
      ))}
    </div>
  );
}
